import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./context/CartContext";
import { apiUrl } from "../config/config";
import image from "../assets/33_360x.webp";
import SEO from "./SEO";
import CartSkeleton from "./CartSkeleton";

const getUserOrGuestId = () => {
  const user = localStorage.getItem("user");

  if (user) {
    try {
      const parsed = JSON.parse(user);
      if (parsed?.id) {
        return { user_id: parsed.id, guest_id: null };
      }
    } catch {}
  }

  let guest_id = localStorage.getItem("guest_id");

  if (!guest_id) {
    guest_id = "guest_" + Math.random().toString(36).substring(2, 12);
    localStorage.setItem("guest_id", guest_id);
  }

  return { user_id: null, guest_id };
};

export default function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { refreshCart } = useCart();

  useEffect(() => {
    const { user_id, guest_id } = getUserOrGuestId();

    const url = user_id
      ? `${apiUrl}/api/cart/user/${user_id}`
      : `${apiUrl}/api/cart?guest_id=${guest_id}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCartItems(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Cart fetch error:", err);
        setLoading(false);
      });
  }, []);

  const deleteItem = async (id) => {
    const { user_id, guest_id } = getUserOrGuestId();

    await fetch(`${apiUrl}/api/cart/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id,
        guest_id,
      }),
    });

    setCartItems((prev) => prev.filter((item) => item.id !== id));
    refreshCart();
  };

  const total = cartItems.reduce((sum, item) => sum + Number(item.price), 0);

  if (loading) return <CartSkeleton />;

  if (cartItems.length === 0) {
    return (
      <>
        <SEO
          title="Cart"
          description="Patch Designers For Hats"
          keywords="Patch Designers For Hats"
        />

        <div className="min-h-[70vh] bg-gradient-to-b from-[#eef6ff] via-white to-[#fff4e8]">
          <div className="mx-auto flex w-full max-w-[900px] flex-col items-center justify-center px-4 py-16 text-center sm:px-6 lg:px-10">
            <div className="rounded-[24px] border border-[#e5eaf0] bg-white px-5 py-10 shadow-[0_18px_50px_rgba(15,35,54,0.10)] sm:px-8 lg:px-12">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#fff7ed] text-[28px]">
                🛒
              </div>

              <h1 className="text-center text-3xl font-bold tracking-wide text-[#2b2b2b] md:text-4xl">
                Your Cart is Empty
              </h1>

              <p className="mx-auto mt-5 max-w-[560px] text-[15px] font-medium leading-[26px] text-[#415164] sm:text-[16px]">
                Looks like you haven’t added anything yet. Browse our products
                and start building your custom patch order.
              </p>

              <a
                href="/products"
                className="mt-7 inline-flex rounded-[14px] bg-[#0a2336] px-7 py-3 text-[15px] font-extrabold uppercase tracking-[0.5px] text-white shadow-[0_12px_30px_rgba(10,35,54,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#f97316]"
              >
                Continue Shopping
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Cart"
        description="Patch designers For Hats"
        keywords="Patch designers For Hats"
      />

      <div className="min-h-screen bg-gradient-to-b from-[#eef6ff] via-white to-[#fff4e8]">
        <div className="mx-auto w-full max-w-[1600px] px-4 py-10 sm:px-6 sm:py-12 lg:px-10 lg:py-14">
          <div className="mb-8 text-center sm:mb-10">
            <h1 className="text-center text-3xl font-bold tracking-wide text-[#2b2b2b] md:text-4xl">
              Shopping Cart
            </h1>

            <div className="mx-auto mt-6 h-px w-full max-w-[620px] bg-[#9ca3af]"></div>

            <p className="mx-auto mt-5 max-w-[760px] text-[15px] font-medium leading-[26px] text-[#415164] sm:text-[16px]">
              Review your custom patch items before moving to checkout.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px] lg:gap-8">
            <div className="overflow-hidden rounded-[24px] border border-[#e5eaf0] bg-white shadow-[0_18px_50px_rgba(15,35,54,0.10)]">
              <div className="hidden grid-cols-12 border-b border-[#e5eaf0] bg-[#f8fafc] px-6 py-4 text-[14px] font-extrabold uppercase tracking-[0.5px] text-[#0a2336] md:grid">
                <div className="col-span-7">Product</div>
                <div className="col-span-3 text-center">Quantity</div>
                <div className="col-span-2 text-right">Total</div>
              </div>

              <div className="flex flex-col gap-4 border-b border-[#e5eaf0] px-4 py-5 md:grid md:grid-cols-12 md:px-6">
                <div className="flex gap-4 md:col-span-7">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-[#e5eaf0] bg-[#F2ECFF] p-2">
                    <img
                      src={image}
                      className="h-full w-full object-contain"
                      alt="Free Proof Approval"
                    />
                  </div>

                  <div>
                    <h3 className="text-[16px] font-extrabold leading-[24px] text-[#0a2336] md:text-[18px]">
                      Free Proof Approval Before Production
                    </h3>

                    <p className="mt-2 text-[14px] font-medium leading-[22px] text-[#415164]">
                      Review your digital proof before your order goes into
                      production.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between font-bold text-[#2b2b2b] md:col-span-3 md:justify-center">
                  <span className="md:hidden">Qty:</span>
                  <span className="rounded-[10px] border border-[#e5eaf0] bg-[#f8fafc] px-4 py-2">
                    1
                  </span>
                </div>

                <div className="flex items-center justify-between font-extrabold text-[#2b2b2b] md:col-span-2 md:justify-end">
                  <span className="md:hidden">Total:</span>
                  <span>$0.00</span>
                </div>
              </div>

              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-5 border-b border-[#e5eaf0] px-4 py-6 last:border-b-0 md:grid md:grid-cols-12 md:px-6"
                >
                  <div className="flex gap-4 md:col-span-7">
                    <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-[16px] border border-[#e5eaf0] bg-[#F2ECFF] p-2">
                      <img
                        src={
                          item.upload_image
                            ? `${apiUrl}/storage/${item.upload_image}`
                            : "/img/no-image.png"
                        }
                        className="h-full w-full object-contain"
                        alt={item.product_name}
                      />
                    </div>

                    <div className="min-w-0">
                      <h3 className="text-[16px] font-extrabold leading-[24px] text-[#0a2336] md:text-[18px]">
                        {item.product_name}
                      </h3>

                      <div className="mt-3 space-y-1 text-[14px] font-medium leading-[22px] text-[#415164]">
                        {item?.width && item?.height && (
                          <>
                            <p>
                              <strong className="text-[#0a2336]">Width:</strong>{" "}
                              {item.width}"
                            </p>
                            <p>
                              <strong className="text-[#0a2336]">
                                Height:
                              </strong>{" "}
                              {item.height}"
                            </p>
                          </>
                        )}

                        {item.selected_filters &&
                          Object.entries(item.selected_filters).map(
                            ([lbl, val]) => (
                              <p key={lbl}>
                                <strong className="text-[#0a2336]">
                                  {lbl}:
                                </strong>{" "}
                                {val}
                              </p>
                            )
                          )}

                        {item.inputs?.border_thread_color && (
                          <p>
                            <strong className="text-[#0a2336]">
                              Thread Color:
                            </strong>{" "}
                            {item.inputs.border_thread_color}
                          </p>
                        )}

                        {item.inputs?.special_notes && (
                          <p>
                            <strong className="text-[#0a2336]">Notes:</strong>{" "}
                            {item.inputs.special_notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:col-span-3 md:justify-center">
                    <span className="font-bold text-[#0a2336] md:hidden">
                      Qty:
                    </span>

                    <div className="flex items-center gap-3">
                      

                      <div className="flex h-10 min-w-[52px] items-center justify-center rounded-[12px] border border-[#e5eaf0] bg-[#f8fafc] px-4 text-[15px] font-extrabold text-[#2b2b2b]">
                        {item.qty}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:col-span-2 md:justify-end">
                    <span className="font-bold text-[#0a2336] md:hidden">
                      Total:
                    </span>

                    <div className="text-[18px] font-extrabold text-[#2b2b2b]">
                      ${item.price}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24 rounded-[24px] border border-[#e5eaf0] bg-white px-5 py-6 shadow-[0_18px_50px_rgba(15,35,54,0.10)] sm:px-6">
                <h2 className="text-[24px] font-extrabold tracking-wide text-[#2b2b2b]">
                  Order Summary
                </h2>

                <div className="mt-5 h-px w-full bg-[#e5eaf0]"></div>

                <div className="mt-5 flex items-center justify-between">
                  <p className="text-[15px] font-bold text-[#415164]">Total</p>
                  <p className="text-[30px] font-extrabold text-[#0a2336]">
                    ${total.toFixed(2)}
                  </p>
                </div>

                <p className="mt-4 rounded-[14px] bg-[#f8fafc] px-4 py-3 text-[13px] font-medium leading-[22px] text-[#415164]">
                  Taxes and shipping calculated at checkout.
                </p>

                <button
                  onClick={() => navigate("/checkout")}
                  disabled={cartItems.length === 0}
                  className={`mt-5 w-full rounded-[14px] px-6 py-3.5 text-[15px] font-extrabold uppercase tracking-[0.5px] text-white shadow-[0_12px_30px_rgba(10,35,54,0.18)] transition-all duration-300 ${
                    cartItems.length === 0
                      ? "cursor-not-allowed bg-gray-400"
                      : "bg-[#0a2336] hover:-translate-y-0.5 hover:bg-[#f97316] hover:shadow-[0_16px_35px_rgba(249,115,22,0.25)]"
                  }`}
                >
                  Checkout
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/products")}
                  className="mt-3 w-full rounded-[14px] border border-[#e5eaf0] bg-white px-6 py-3 text-[14px] font-bold text-[#2b2b2b] transition-all duration-300 hover:border-[#f97316]/50 hover:text-[#f97316]"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}